import { Middleware } from "@reduxjs/toolkit";
import { CALL_HISTORY_METHOD } from "connected-react-router";
import {
  addLanguageToPath,
  isChinaDomainClient,
  saveLanguagePreference,
  extractLanguageFromPath,
} from "@Utils/languageCore";

/**
 * Redux middleware that adds language prefix to router navigation
 *
 * Language Priority:
 * 1. URL prefix (highest) - if path has /fr/, use French
 * 2. Redux store - use userLanguage from Redux (set by user choice or URL)
 * 3. Default "en" - if nothing else is available
 *
 * NOTE: We do NOT use cookies directly to avoid automatic redirects.
 * The LanguageRedirectGuard handles cookie-based suggestions via a modal.
 */
const languageRouterMiddleware: Middleware = (store) => (next) => (action: any) => {
  if (action.type === CALL_HISTORY_METHOD) {
    const { payload } = action;
    const state = store.getState();

    if (payload?.method === "push" || payload?.method === "replace") {
      const [path, ...rest] = payload.args;

      if (typeof path === "string") {
        // Check if this is China domain - skip language prefix for China
        const isChinaDomain = isChinaDomainClient();

        // For China domain, never add language prefix (Chinese is default)
        if (isChinaDomain) {
          return next(action);
        }

        // Priority 1: Check if path already has a language prefix
        const pathLanguage = extractLanguageFromPath(path);
        if (pathLanguage) {
          // Path has language prefix - use it and save to cookie
          saveLanguagePreference(pathLanguage);
          return next(action);
        }

        // Priority 2: Check Redux store for user language
        const storeLanguage = state.appData?.userLanguage;
        if (storeLanguage && storeLanguage !== "en") {
          // Store has non-English language - use it
          const enhancedPath = addLanguageToPath(path, storeLanguage);
          return next({
            ...action,
            payload: {
              ...payload,
              args: [enhancedPath, ...rest],
            },
          });
        }

        // Priority 3: Default to English (no prefix needed)
        // English doesn't need prefix, return path as-is
        return next(action);
      }
    }
  }

  return next(action);
};

export default languageRouterMiddleware;
